import React, { useState } from 'react';
import { api } from '../lib/api';
import { fmtDate } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { Camera } from '../lib/types';
import { Confirm, Drawer, Failed, Loading, type Ctx } from '../main';

/**
 * The cameras on each property — the register, not the picture.
 *
 * Live view needs a media gateway that turns each camera's RTSP stream into
 * something a browser can play, behind the same authorisation as this console.
 * It is not part of this release, and this screen says so rather than showing a
 * black box with "LIVE" on it. What is here is real: which camera is where, and
 * whether it has a password on file. The password itself never leaves the API.
 */
export function Security({ ctx }: { ctx: Ctx }): React.JSX.Element {
  const { data, loading, error, reload } = useResource(() => api.cameras(), []);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<Camera | null>(null);

  if (loading) return <Loading />;
  if (error) return <Failed error={error} onRetry={reload} />;
  const cameras = data?.cameras ?? [];
  const sites = ctx.properties.filter((p) => cameras.some((c) => c.property_id === p.id));

  return (
    <>
      <div className="note warn" style={{ marginBottom: 14 }}>
        <b>Live view is not in this release.</b> It needs the media gateway, which turns each camera’s
        stream into something a browser can play. This is the register it will read from.
      </div>
      <div className="bar">
        <span className="sub">{cameras.length} camera{cameras.length === 1 ? '' : 's'} across {sites.length} site{sites.length === 1 ? '' : 's'}</span>
        {ctx.canManage && (
          <button className="btn sm accent w" style={{ marginLeft: 'auto' }} onClick={() => setAdding(true)}>Add a camera</button>
        )}
      </div>

      {sites.map((p) => (
        <div key={p.id} style={{ marginBottom: 18 }}>
          <h2 className="sec" style={{ marginTop: 8 }}>{p.name}</h2>
          <div className="grid g3">
            {cameras.filter((c) => c.property_id === p.id).map((c) => (
              <div className="card" key={c.id}><div className="bd">
                <div className="feed"><span>{c.location}</span></div>
                <div className="between" style={{ marginTop: 10 }}>
                  <b>{c.location}</b>
                  {ctx.canManage && (
                    <button className="btn sm quiet w" style={{ color: 'var(--coral)' }} onClick={() => setRemoving(c)}>Remove</button>
                  )}
                </div>
                <div className="tid" style={{ wordBreak: 'break-all', marginTop: 4 }}>{c.stream_url}</div>
                <div className="meta" style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  <span className={`tag ${c.has_credentials ? 't-ok' : 't-warn'}`}>
                    {c.has_credentials ? 'Password on file' : 'No password'}
                  </span>
                  <span className="tag t-mute">{c.last_seen ? `Seen ${fmtDate(c.last_seen)}` : 'Never seen'}</span>
                </div>
              </div></div>
            ))}
          </div>
        </div>
      ))}
      {cameras.length === 0 && (
        <div className="card"><div className="empty"><b>No cameras yet</b>Add one to keep a record of what covers the shared areas.</div></div>
      )}

      {adding && <AddCamera ctx={ctx} onClose={() => setAdding(false)}
        onAdded={async () => { setAdding(false); await reload(); ctx.toast('Camera added'); }} />}
      {removing && (
        <Confirm title={`Remove ${removing.location}?`} danger action="Remove camera"
          detail="It comes off the register. The camera itself is untouched."
          onClose={() => setRemoving(null)}
          onConfirm={async () => { await api.deleteCamera(removing.id); setRemoving(null); await reload(); ctx.toast('Camera removed'); }} />
      )}
    </>
  );
}

function AddCamera({ ctx, onClose, onAdded }: {
  ctx: Ctx; onClose: () => void; onAdded: () => Promise<void>;
}): React.JSX.Element {
  const [form, setForm] = useState({ propertyId: ctx.properties[0]?.id ?? '', location: '', streamUrl: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = /^(rtsp|rtsps|https?):\/\/\S+$/i.test(form.streamUrl);

  const save = async (): Promise<void> => {
    setBusy(true); setError(null);
    try { await api.createCamera(form); await onAdded(); }
    catch (e) { setError((e as Error).message); setBusy(false); }
  };

  return (
    <Drawer title="Add a camera" onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn accent" disabled={!form.propertyId || !form.location || !valid || busy} onClick={save}>
          {busy ? 'Saving…' : 'Add camera'}
        </button>
      </>}>
      {error && <div className="note bad" style={{ marginBottom: 14 }}>{error}</div>}
      <div className="field">
        <label htmlFor="c-p">Property</label>
        <select id="c-p" value={form.propertyId} onChange={(e) => setForm({ ...form, propertyId: e.target.value })}>
          {ctx.properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div className="field">
        <label htmlFor="c-l">Where it points</label>
        <input id="c-l" placeholder="Lobby entrance" value={form.location}
          onChange={(e) => setForm({ ...form, location: e.target.value })} />
      </div>
      <div className="field">
        <label htmlFor="c-u">Stream URL</label>
        <input id="c-u" placeholder="rtsp://user:password@10.20.0.15:554/stream1" value={form.streamUrl}
          onChange={(e) => setForm({ ...form, streamUrl: e.target.value })} />
        <div className="hint">
          Include the camera’s username and password if it has one. They are stored for the gateway and
          never shown again — not here, not to anyone.
        </div>
      </div>
    </Drawer>
  );
}
